import { COLOR_MODE_LABELS, COLOR_MODES, type ColorMode, type EntityStyle } from '@hoopoe/shared';
import { Palette } from 'lucide-react';
import { useId } from 'react';
import { cn } from '../../lib/cn';
import { useColorMode } from '../../lib/colors';
import { Swatch, slotName } from '../schema/color-slot-picker';
import { Tooltip } from '../ui/tooltip';

const MODE_HELP: Record<ColorMode, string> = {
  type: 'Each entity takes its type’s color (set by an admin in the schema).',
  cluster: 'Entities that are closely linked share a color: clusters are found in the relation graph every night.',
  rules: 'Admin-defined rules, first match wins (e.g. Color by Camp). Hover any mark for “why this color?”.',
};

/**
 * Toolbar switch for the "Color by" mode (PLAN §12.2): remembered per user, applies to every screen. A segmented
 * radio group, so each mode's meaning is one hover or focus away.
 */
export function ColorBySwitch({ className }: { className?: string }) {
  const { mode, setMode } = useColorMode();
  const name = useId();
  return (
    <fieldset className={cn('flex items-center gap-1.5 text-sm', className)} data-testid="color-by">
      <legend className="sr-only">Color by</legend>
      <Palette className="size-4 text-muted-foreground" aria-hidden="true" />
      <span aria-hidden="true" className="text-muted-foreground">
        Color by
      </span>
      <div className="flex rounded-md border border-input bg-surface p-0.5">
        {COLOR_MODES.map(m => (
          <Tooltip key={m} content={MODE_HELP[m]}>
            <label className="cursor-pointer rounded px-2 py-0.5 text-muted-foreground has-[:checked]:bg-muted has-[:checked]:font-medium has-[:checked]:text-foreground has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring">
              <input type="radio" name={name} value={m} className="sr-only" checked={mode === m} onChange={() => setMode(m)} />
              {COLOR_MODE_LABELS[m]}
            </label>
          </Tooltip>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Legend for a visualization (PLAN §12.2 "Every visualization shows a legend"): one row per color in use, labelled
 * with what it means in the active mode ("Rule #1: Color by Camp · Camp = Adverse", "Color by type: Media").
 */
export function ColorLegend({ styles, className }: { styles: EntityStyle[]; className?: string }) {
  const rows = new Map<string, { slot: number | null; label: string; count: number }>();
  for (const s of styles) {
    const key = `${s.colorSlot ?? 'none'}|${s.why}`;
    const row = rows.get(key) ?? { slot: s.colorSlot, label: s.why, count: 0 };
    row.count++;
    rows.set(key, row);
  }
  const faded = styles.filter(s => s.fade);
  const ringed = styles.filter(s => s.ring);
  return (
    <ul aria-label="Legend" className={cn('space-y-1 text-xs', className)}>
      {[...rows.values()].map(r => (
        <li key={`${r.slot}|${r.label}`} className="flex items-center gap-2" data-legend-slot={r.slot ?? 'none'}>
          <Swatch slot={r.slot} />
          <span className="sr-only">{slotName(r.slot)}</span>
          <span className="min-w-0 truncate">{r.label}</span>
          <span className="ms-auto text-muted-foreground tabular">{r.count}</span>
        </li>
      ))}
      {faded.length > 0 && (
        <li className="flex items-center gap-2 text-muted-foreground">
          <span aria-hidden="true" className="inline-block size-3.5 rounded-full border border-foreground/40 opacity-40" />
          Faded: {faded[0]?.fadeWhy}
          <span className="ms-auto tabular">{faded.length}</span>
        </li>
      )}
      {ringed.length > 0 && (
        <li className="flex items-center gap-2 text-muted-foreground">
          <span aria-hidden="true" className="inline-block size-3.5 rounded-full border-2 border-foreground" />
          Ring: {ringed[0]?.ringWhy}
          <span className="ms-auto tabular">{ringed.length}</span>
        </li>
      )}
    </ul>
  );
}
